/**
 * daily-report.ts
 * add-locx-daily-report Slice 3 — "parte diario de obra" header contracts (SL-51).
 *
 * Header-only: line tables (transport/equipment/personnel) and attachments
 * are added in later slices (4/5/6) as their own contract files.
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// WeatherConditionSchema — closed enum (SL-51.1, SL-51.5)
// ---------------------------------------------------------------------------
// Fixed, non-tenant-varying vocabulary — not EAV (LOCX CLAUDE.md red line #4).
// ---------------------------------------------------------------------------

export const WeatherConditionSchema = z.enum(['bueno', 'malo'], {
  errorMap: () => ({ message: "weatherCondition must be 'bueno' or 'malo'" }),
});

export type WeatherCondition = z.infer<typeof WeatherConditionSchema>;

// ---------------------------------------------------------------------------
// Line-table closed enums (add-locx-daily-report Slice 4 — SL-52, SL-53)
// ---------------------------------------------------------------------------

export const ModalitySchema = z.enum(['propio', 'subcontratado'], {
  errorMap: () => ({ message: "modality must be 'propio' or 'subcontratado'" }),
});
export type Modality = z.infer<typeof ModalitySchema>;

export const EquipmentLocationSchema = z.enum(['obra', 'cantera'], {
  errorMap: () => ({ message: "location must be 'obra' or 'cantera'" }),
});
export type EquipmentLocation = z.infer<typeof EquipmentLocationSchema>;

export const OwnershipSchema = z.enum(['propio', 'alquilado'], {
  errorMap: () => ({ message: "ownership must be 'propio' or 'alquilado'" }),
});
export type Ownership = z.infer<typeof OwnershipSchema>;

// ---------------------------------------------------------------------------
// TransportLineCreateSchema (SL-52) — PAD 3.1+3.2
// ---------------------------------------------------------------------------
// serviceItemId is mandatory — identifies WHICH billable material this line
// transports; there is no free-text `material` field (SL-52.2). totalM3 is
// NOT accepted from the body — computed server-side as unitVolumeM3 *
// tripCount (SL-52.3).
//
// driverEmploymentId (SL-69.1/SL-70.1/SL-70.2, add-daily-report-line-identity-links)
// is OPTIONAL — when omitted, driverEmploymentId is resolved server-side via
// IEmployeeLookup.lookupByEmployeeNumber, best-effort (SL-52-A, unchanged).
// When PRESENT, it wins: the use-case validates it belongs to this tenant
// (rejects the whole submission otherwise) and OVERWRITES driverName/
// driverEmployeeNumber with the resolved record — client-sent text for those
// two fields is discarded when an id is supplied (design.md §2).
//
// plateAssetId (SL-69.2/SL-70.3, same change) is OPTIONAL, references
// servicios.asset — same id-wins-and-overwrites-plateNumber treatment, and
// same tenant-validated-before-insert discipline as equipment's assetId
// (SL-53.4).
// ---------------------------------------------------------------------------

export const TransportLineCreateSchema = z.object({
  serviceItemId: z.string().uuid('serviceItemId must be a valid UUID'),
  cargadero: z.string().optional().nullable(),
  driverName: z.string().optional().nullable(),
  driverEmployeeNumber: z.string().optional().nullable(),
  driverEmploymentId: z.string().uuid('driverEmploymentId must be a valid UUID').optional().nullable(),
  modality: ModalitySchema,
  plateNumber: z.string().optional().nullable(),
  plateAssetId: z.string().uuid('plateAssetId must be a valid UUID').optional().nullable(),
  unitType: z.string().optional().nullable(),
  destination: z.string().optional().nullable(),
  unitVolumeM3: z.coerce.number().nonnegative('unitVolumeM3 must be ≥ 0'),
  tripCount: z.coerce.number().int().nonnegative('tripCount must be ≥ 0'),
});

export type TransportLineCreate = z.infer<typeof TransportLineCreateSchema>;

export const TransportLineResponseSchema = z.object({
  id: z.string().uuid(),
  dailyReportId: z.string().uuid(),
  serviceItemId: z.string().uuid(),
  cargadero: z.string().nullable(),
  driverName: z.string().nullable(),
  driverEmployeeNumber: z.string().nullable(),
  driverEmploymentId: z.string().uuid().nullable(),
  modality: ModalitySchema,
  plateNumber: z.string().nullable(),
  plateAssetId: z.string().uuid().nullable(),
  unitType: z.string().nullable(),
  destination: z.string().nullable(),
  unitVolumeM3: z.string(),
  tripCount: z.number().int(),
  totalM3: z.string(),
  createdAt: z.string().datetime().or(z.date()),
});

export type TransportLineResponse = z.infer<typeof TransportLineResponseSchema>;

// ---------------------------------------------------------------------------
// EquipmentLineCreateSchema (SL-53) — PAD 3.3+3.4
// ---------------------------------------------------------------------------
// serviceItemId is mandatory — identifies the billable EQUIPMENT CATEGORY,
// independent of assetId (the specific physical unit, optional). ownership is
// NOT accepted from the body — resolved read-side from the asset (when
// assetId is set) or from ownershipOverride (when it isn't) — SL-53.2/SL-53.3.
//
// operatorEmploymentId (SL-69.3/SL-70.3, add-daily-report-line-identity-links)
// is OPTIONAL, same id-wins-and-overwrites-operatorName/operatorEmployeeNumber
// treatment as transport's driverEmploymentId (design.md §2/§3 of that change).
// ---------------------------------------------------------------------------

export const EquipmentLineCreateSchema = z.object({
  location: EquipmentLocationSchema,
  serviceItemId: z.string().uuid('serviceItemId must be a valid UUID'),
  quantity: z.coerce.number().positive('quantity must be > 0').default(1),
  operatorName: z.string().optional().nullable(),
  operatorEmployeeNumber: z.string().optional().nullable(),
  operatorEmploymentId: z.string().uuid('operatorEmploymentId must be a valid UUID').optional().nullable(),
  equipmentDescription: z.string().optional().nullable(),
  internalCode: z.string().optional().nullable(),
  assetId: z.string().uuid().optional().nullable(),
  ownershipOverride: OwnershipSchema.optional().nullable(),
  tasks: z.string().optional().nullable(),
  observations: z.string().optional().nullable(),
});

export type EquipmentLineCreate = z.infer<typeof EquipmentLineCreateSchema>;

export const EquipmentLineResponseSchema = z.object({
  id: z.string().uuid(),
  dailyReportId: z.string().uuid(),
  location: EquipmentLocationSchema,
  serviceItemId: z.string().uuid(),
  quantity: z.string(),
  operatorName: z.string().nullable(),
  operatorEmployeeNumber: z.string().nullable(),
  operatorEmploymentId: z.string().uuid().nullable(),
  equipmentDescription: z.string().nullable(),
  internalCode: z.string().nullable(),
  assetId: z.string().uuid().nullable(),
  ownershipOverride: OwnershipSchema.nullable(),
  /** Resolved ownership (SL-53.2/SL-53.3) — asset's attribute wins when assetId is set. */
  resolvedOwnership: OwnershipSchema.nullable(),
  tasks: z.string().nullable(),
  observations: z.string().nullable(),
  createdAt: z.string().datetime().or(z.date()),
});

export type EquipmentLineResponse = z.infer<typeof EquipmentLineResponseSchema>;

// ---------------------------------------------------------------------------
// PersonnelLineCreateSchema (SL-54) — PAD 3.5+3.5b
// ---------------------------------------------------------------------------
// serviceItemId is MANDATORY iff category='cuadrilla' (billable jornal), and
// MUST be omitted iff category='indirecto' (overhead, never billed) — SL-54.3,
// enforced here via superRefine (defense-in-depth: also re-checked by
// resolvePersonnelLine in the application layer).
//
// employmentId (SL-69.4/SL-70.3, add-daily-report-line-identity-links) is
// OPTIONAL, same id-wins-and-overwrites-fullName/employeeNumber treatment as
// transport's driverEmploymentId — but validated INLINE in resolvePersonnelLine
// itself, not in CreateDailyReportUseCase's batch step (design.md §4 of that
// change — resolvePersonnelLine runs earlier in the control flow).
// ---------------------------------------------------------------------------

export const PersonnelCategorySchema = z.enum(['cuadrilla', 'indirecto'], {
  errorMap: () => ({ message: "category must be 'cuadrilla' or 'indirecto'" }),
});
export type PersonnelCategory = z.infer<typeof PersonnelCategorySchema>;

export const PersonnelLineCreateSchema = z
  .object({
    category: PersonnelCategorySchema,
    serviceItemId: z.string().uuid().optional().nullable(),
    quantity: z.coerce.number().positive('quantity must be > 0').default(1),
    fullName: z.string().optional().nullable(),
    employeeNumber: z.string().optional().nullable(),
    employmentId: z.string().uuid('employmentId must be a valid UUID').optional().nullable(),
    roleOrTask: z.string().optional().nullable(),
    lightVehicleType: z.string().optional().nullable(),
    plateNumber: z.string().optional().nullable(),
    observations: z.string().optional().nullable(),
  })
  .superRefine((data, ctx) => {
    if (data.category === 'cuadrilla' && !data.serviceItemId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['serviceItemId'],
        message: "serviceItemId is required when category='cuadrilla'",
      });
    }
    if (data.category === 'indirecto' && data.serviceItemId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['serviceItemId'],
        message: "serviceItemId must be omitted when category='indirecto'",
      });
    }
  });

export type PersonnelLineCreate = z.infer<typeof PersonnelLineCreateSchema>;

export const PersonnelLineResponseSchema = z.object({
  id: z.string().uuid(),
  dailyReportId: z.string().uuid(),
  category: PersonnelCategorySchema,
  serviceItemId: z.string().uuid().nullable(),
  quantity: z.string(),
  fullName: z.string().nullable(),
  employeeNumber: z.string().nullable(),
  employmentId: z.string().uuid().nullable(),
  /** Free text; defaulted read-side from the resolved employment's position when blank (SL-54.2) — not yet wired to a route. */
  roleOrTask: z.string().nullable(),
  lightVehicleType: z.string().nullable(),
  plateNumber: z.string().nullable(),
  observations: z.string().nullable(),
  createdAt: z.string().datetime().or(z.date()),
});

export type PersonnelLineResponse = z.infer<typeof PersonnelLineResponseSchema>;

// ---------------------------------------------------------------------------
// DailyReportCreateSchema — POST body (header + line arrays)
// ---------------------------------------------------------------------------
// reportNumber is NOT accepted from the body — assigned by the use-case as
// MAX+1 per service order (SL-51.3).
// supervisorUserId is NOT accepted from the body — set from TenantContext.userId
// at write time (SL-51.4).
// transportLines/equipmentLines/personnelLines are all optional — a report
// with only avance (or a bare header) is valid.
// ---------------------------------------------------------------------------

export const DailyReportCreateSchema = z.object({
  reportDate: z.string().date('reportDate must be a valid ISO date string (YYYY-MM-DD)'),
  weatherCondition: WeatherConditionSchema,
  hasWind: z.boolean().default(false),
  hasRain: z.boolean().default(false),
  hasFog: z.boolean().default(false),
  hasSnow: z.boolean().default(false),
  activitiesText: z.string().optional().nullable(),
  nextDayTasksText: z.string().optional().nullable(),
  observationsText: z.string().optional().nullable(),
  inspectorName: z.string().max(256, 'inspectorName must be at most 256 characters').optional().nullable(),
  inspectorSignedAt: z
    .string()
    .datetime({ message: 'inspectorSignedAt must be a valid ISO datetime string' })
    .optional()
    .nullable(),
  transportLines: z.array(TransportLineCreateSchema).optional(),
  equipmentLines: z.array(EquipmentLineCreateSchema).optional(),
  personnelLines: z.array(PersonnelLineCreateSchema).optional(),
  /**
   * Path A (SL-56.4) — direct progress entry, keyed by serviceItemId → qty.
   * For entryMode='absolute' items (avance %) submitted straight from the
   * PAD's avance matrix, not derived from a line quantity.
   */
  progress: z.record(z.string().uuid(), z.coerce.number()).optional(),
});

export type DailyReportCreate = z.infer<typeof DailyReportCreateSchema>;

// ---------------------------------------------------------------------------
// DailyReportAttachmentResponseSchema (SL-55) — add-locx-daily-report Slice 6
// ---------------------------------------------------------------------------
// Metadata resolved via lookupFileById (KI-8) — never duplicated in this
// module's own tables (KI-6.3). storageRef is intentionally excluded (internal
// detail of the kernel file-service, not for client consumption).
// ---------------------------------------------------------------------------

export const DailyReportAttachmentResponseSchema = z.object({
  id: z.string().uuid(),
  fileId: z.string().uuid(),
  originalName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number(),
  createdAt: z.string().datetime().or(z.date()),
});

export type DailyReportAttachmentResponse = z.infer<typeof DailyReportAttachmentResponseSchema>;

// ---------------------------------------------------------------------------
// AttachDailyReportPhotosSchema — POST /servicios/daily-reports/:id/attachments
// ---------------------------------------------------------------------------
// fileIds reference kernel files ALREADY uploaded via the existing file-service
// (SL-55.2 — this change introduces zero new upload/storage code).
// ---------------------------------------------------------------------------

export const AttachDailyReportPhotosSchema = z
  .object({
    fileIds: z.array(z.string().uuid()).min(1, 'At least one fileId is required'),
  })
  .strict();

export type AttachDailyReportPhotosInput = z.infer<typeof AttachDailyReportPhotosSchema>;

// ---------------------------------------------------------------------------
// DailyReportResponseSchema — conforms to SL-6 (typed, no raw EAV)
// ---------------------------------------------------------------------------

export const DailyReportResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  serviceOrderId: z.string().uuid(),
  reportNumber: z.number().int(),
  reportDate: z.string(),
  weatherCondition: WeatherConditionSchema,
  hasWind: z.boolean(),
  hasRain: z.boolean(),
  hasFog: z.boolean(),
  hasSnow: z.boolean(),
  activitiesText: z.string().nullable(),
  nextDayTasksText: z.string().nullable(),
  observationsText: z.string().nullable(),
  inspectorName: z.string().nullable(),
  inspectorSignedAt: z.string().nullable(),
  supervisorUserId: z.string().uuid(),
  supervisorSignedAt: z.string().nullable(),
  createdAt: z.string().datetime().or(z.date()),
  updatedAt: z.string().datetime().or(z.date()),
  attachments: z.array(DailyReportAttachmentResponseSchema),
});

export type DailyReportResponse = z.infer<typeof DailyReportResponseSchema>;

// ---------------------------------------------------------------------------
// DailyReportDetailResponseSchema (SL-61) — add-locx-daily-report-frontend Slice 0
// ---------------------------------------------------------------------------
// Extends the header+attachments shape with the three line arrays. Used ONLY by
// GET /servicios/daily-reports/:id — the paginated list endpoint deliberately
// keeps returning DailyReportResponseSchema (header + attachments only) to avoid
// an N+1 fetch of three line tables per page row (SL-61.2).
// ---------------------------------------------------------------------------

export const DailyReportDetailResponseSchema = DailyReportResponseSchema.extend({
  transportLines: z.array(TransportLineResponseSchema),
  equipmentLines: z.array(EquipmentLineResponseSchema),
  personnelLines: z.array(PersonnelLineResponseSchema),
});

export type DailyReportDetailResponse = z.infer<typeof DailyReportDetailResponseSchema>;
